// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import type { PurchaseState, ShoppingListItem, ShoppingListResponse, ShoppingPurchaseRequest } from "@bitebalance/contracts"
import { countNounOf, countWord, numText } from "./buyLine"

/**
 * Shop → Pantry v1 (pre-beta program WS3) — the web view-model of a purchase
 * confirmation, mirrored by mobile/src/lib/shopPurchase.ts so both clients
 * read the SAME served state the same way:
 *
 *   bought       — the confirmed packs entered the Pantry;
 *   already_had  — the typed quantity (or nothing) entered the Pantry, the
 *                  row left the to-buy total;
 *   not_bought   — nothing entered the Pantry, the row stays open.
 *
 * Nothing here decides quantities — the server defaults a bought row to the
 * packs on the list and applies corrections as differences; the client only
 * names the state and shows what the server confirmed.
 */

export type TFn = (key: string, vars?: Record<string, string | number>) => string

export interface PurchaseChoice {
  state: PurchaseState
  label: string
  selected: boolean
}

export function purchaseChoices(item: ShoppingListItem, t: TFn): PurchaseChoice[] {
  const current = item.purchase?.state ?? null
  return [
    { state: "bought", label: t("views.shop.purchaseBought"), selected: current === "bought" },
    { state: "already_had", label: t("views.shop.purchaseAlreadyHad"), selected: current === "already_had" },
    { state: "not_bought", label: t("views.shop.purchaseNotBought"), selected: current === "not_bought" },
  ]
}

export function purchasedQuantityText(item: ShoppingListItem, t: TFn): string | null {
  const q = item.purchase?.quantity
  if (!q) return null
  const { amount, unit } = q.canonical
  // Shop list clarity (2026-10-06): a count says what is counted ("9 Stück", "10 Zehen"), like the row it came from;
  // an absent or unknown count noun keeps the plain "9×". Numbers print in the reader's decimal mark.
  if (unit === "count") {
    const noun = countNounOf(item)
    const word = noun ? countWord(noun, amount, t) : null
    return word ? `${numText(amount, t)} ${word}` : `${numText(amount, t)}×`
  }
  if (unit === "g" && amount >= 1000) return `${numText(amount / 1000, t)} kg`
  if (unit === "ml" && amount >= 1000) return `${numText(amount / 1000, t)} l`
  return `${numText(amount, t)} ${unit}`
}

export function purchaseStatusText(item: ShoppingListItem, t: TFn): string | null {
  const purchase = item.purchase
  if (!purchase) return null
  const qty = purchasedQuantityText(item, t)
  if (purchase.state === "bought") return t("views.shop.purchaseStateBought", { qty: qty ?? "0" })
  if (purchase.state === "already_had") {
    return qty && purchase.quantity && purchase.quantity.canonical.amount > 0
      ? t("views.shop.purchaseStateAlreadyHad", { qty })
      : t("views.shop.purchaseStateAlreadyHadNone")
  }
  return t("views.shop.purchaseStateNotBought")
}

export function shopDoneTargets(items: ShoppingListItem[]): ShoppingListItem[] {
  return items.filter(item => !item.manual && !item.purchase && (item.buyQuantity ?? 0) > 0)
}

export function allResolved(items: ShoppingListItem[]): boolean {
  return shopDoneTargets(items).length === 0
}

export function purchaseRequest(
  list: ShoppingListResponse,
  itemId: string,
  state: PurchaseState,
  quantity?: number
): ShoppingPurchaseRequest {
  return {
    baseRevision: list.revision,
    items: [
      { itemId, state, ...(quantity !== undefined && Number.isFinite(quantity) && quantity >= 0 ? { quantity } : {}) },
    ],
  }
}

export function shopDoneRequest(list: ShoppingListResponse): ShoppingPurchaseRequest {
  return { baseRevision: list.revision, shopDone: true }
}

export function pantrySyncText(sync: ShoppingListResponse["pantrySync"], t: TFn): string | null {
  if (!sync) return null
  if (sync.addedIngredients === 0 && sync.adjustedIngredients === 0) return t("views.shop.purchaseSyncedNone")
  return t("views.shop.purchaseSynced", { added: sync.addedIngredients, adjusted: sync.adjustedIngredients })
}
